import ActivityBasedTimer from '../services/ActivityBasedTimer'
import axios from 'axios';

/**
 * The UpdateAppMixin watches for changes to the App.
 *
 * We want to listen to updates to the service worker. This happens on navigation within the service worker's scope, or
 * when events such as push and sync, unless there's been an update check within the previous 24 hours.
 * (see https://developer.chrome.com/docs/workbox/service-worker-lifecycle#when_updates_happen)
 *
 * However, the user may have the SPA open for a long time and not navigate, so we have an activity based timer which
 * checks for a new version when instructed (e.g. route change), or at least once a day.
 *
 * When we notice a change, we force an update to the service worker. This will then dispatch an event back here,
 * and we can show a dialog to the user telling them there is an update, and invoke refreshApp() to skip waiting,
 * which will then cause the controller to change and reload the page.
 *
 * Note: this relies upon you adding the following to the updated() hook in the service worker registration:
 *      document.dispatchEvent(new CustomEvent('swUpdated', {detail: registration}));
 *
 * Based on ideas from these URLs:
 * https://dev.to/drbragg/handling-service-worker-updates-in-your-vue-pwa-1pip
 * https://dev.to/originalexe/can-you-please-refresh-or-how-we-version-our-single-page-application-335l
 */
export const updateAppMixin = {
    data() {
        return {
            filename: '/index.html',
            currentHash: null,
            versioningTimer: null,
            showNewVersionDialog: false,
            refreshing: false,
            serviceWorkerRegistration: null,
        }
    },
    created() {
        // Listen for our custom event from the SW registration
        document.addEventListener('swUpdated', this.updateAvailable, { once: true })

        if ('serviceWorker' in navigator) {
            // Prevent multiple refreshes
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (this.refreshing) {
                    return;
                }
                this.refreshing = true;
                // Here the actual reload of the page occurs
                window.location.reload();
            })
        }
    },
    methods: {
        initVersionCheck: async function() {
            this.versioningTimer = new ActivityBasedTimer();
            this.currentHash = await this.getFileHash();

            const self = this;
            this.versioningTimer.setInterval({
                async callback() {
                    console.log('Version callback entered');
                    const newVersionAvailable = await self.isNewerVersionAvailable();
                    console.log('Finished callback');
                    if (!newVersionAvailable) {
                        console.log('No new version');
                        return;
                    }

                    console.log('New version available - notify');
                    if ('serviceWorker' in navigator) {
                        navigator.serviceWorker.ready.then(function (registration) {
                            registration.update();
                        });
                    } else {
                        this.showNewVersionDialog = true;
                    }
                },
                // Normal interval is once every 10 minutes
                // interval: 1000 * 60 * 10,
                interval: 1000 * 60 * 1,

                // Forced interval is once per day
                forcedInterval: 1000 * 60 * 60 * 24,
            });
        },
        isNewerVersionAvailable: async function() {
            console.log("Checking for new version of app");
            const hash = await this.getFileHash();
            // if we can't get a hash, assume it's not changed
            if (!hash) {
                return false;
            }
            // if we haven't yet stored a hash, do so
            if (!this.currentHash) {
                this.currentHash = hash;
                return false;
            }
            return this.currentHash !== hash;
        },
        getFileHash: async function() {
            try {
                const fileResponse = await axios.get(`${this.filename}?t=${new Date().getTime()}`);
                return fileResponse.data.hashCode();
            } catch (error) {
                if (!error.response) {
                    console.log('Error: Network Error');
                } else if (error.response.status === 404) {
                    console.log('Error: File not found');
                } else {
                    console.log('Error: ' + error.response.data.message);
                }
                return null;
            }
        },
        updateAvailable(event) {
            console.log(event)
            // store the SW registration so we can send it a message
            this.serviceWorkerRegistration = event.detail;
            this.showNewVersionDialog = true;
        },
        refreshApp() {
            console.log("refreshing app")
            this.showNewVersionDialog = false
            if ('serviceWorker' in navigator) {
                // make sure we only send a 'skip waiting' message if the SW is waiting
                if (!this.serviceWorkerRegistration || !this.serviceWorkerRegistration.waiting) {
                    return
                }
                // send message to SW to skip the waiting and activate the new SW
                this.serviceWorkerRegistration.waiting.postMessage({type: 'SKIP_WAITING'})
            } else {
                window.location.reload();
            }
        },
    }
};